import React, { useState, useEffect } from "react";
import { Container, Row, Col, Modal, Button, Card, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
import SchoolRollNumberInput from "../components/SchoolRollNumberInput";
import RemovingTeacherModal from "../components/RemovingTeacherModal";
import ChangeAlpacaLeadModal from "../components/ChangeAlpacaLeadModal";
import AddTeachersModal from "../components/AddTeachersModal";
import EditTeacherModal from "../components/EditTeacherModal";
import CreateSchoolsModal from "../components/CreateSchoolsModal";
import BulkGradeTransitionModal from "../components/BulkGradeTransitionModal";
import SwitchSchoolAccessModal from "../components/SwitchSchoolAccessModal";
import SchoolFeatureTagsModal from "../components/SchoolFeatureTagsModal";
import TeacherChatbotAccessModal from "../components/TeacherChatbotAccessModal";


import "../components/SchoolAdmin.css";
import {
  FaUserMinus,
  FaUserPlus,
  FaUserCog,
  FaShieldAlt,
  FaEnvelope,
  FaChartBar,
  FaUserEdit,
  FaSchool,
  FaGraduationCap
} from "react-icons/fa";
import { Clock, CreditCard, LogIn, Bot } from "lucide-react";

import {
  whoami,
  getClassesBySchoolId,
  getTeachersBySchoolId,
  getSchools,
  getCountryGrades,
  getRoles,
  shareClass,
  addAdditionalColleagues,
  createSchoolsBatch,
  getCountries,
  getGradeLevel3FeatureAccessBySchoolId,
  createGradeLevel3FeatureAccess,
  deactivateGradeLevel3FeatureAccess,
  getTagDefinitions,
  getEntityTags,
  updateEntityTag
} from "../services/APIClient";
import { isTeacher, getUserRole } from "../utils/auth";

function SchoolAdmin() {
  const navigate = useNavigate();
  const [teacherid, setTeacherid] = useState(101);
  const [name, setName] = useState("John Doe");
  const [schoolid, setSchoolid] = useState("");
  const [schoolName, setSchoolName] = useState("Loading school information...");
  const [rollnumber, setRollnumber] = useState("");
  const [countryId, setCountryId] = useState("");
  const [staffCurrentSchoolId, setStaffCurrentSchoolId] = useState("");
  const [isPortrait, setIsPortrait] = useState(window.matchMedia("(orientation: portrait)").matches);
  const [gradeLevel3Access, setGradeLevel3Access] = useState([]);
  const [gradeLevel3Loading, setGradeLevel3Loading] = useState(false);
  const [gradeLevel3Error, setGradeLevel3Error] = useState(null);
  const [isLoadingSchool, setIsLoadingSchool] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const teacherMode = isTeacher();

  const isSchoolFound = schoolid && schoolid !== "" && schoolName !== "Loading school information..." && schoolName !== "Please enter school roll number and press 'Find School'";

  console.log("🔍 RENDER STATE:", {
    teacherMode,
    schoolid,
    schoolName,
    isSchoolFound,
    isLoadingSchool,
    loadError
  });

  const [showModal, setShowModal] = useState({
    removingTeacher: false,
    addingTeacher: false,
    editingTeacher: false,
    changingAlpacaLead: false,
    rolesPermissions: false,
    sendForgottenPassword: false,
    viewMetrics: false,
    createSchools: false,
    bulkGradeTransition: false,
    switchSchoolAccess: false,
    schoolFeatureTags: false,
    teacherChatbotAccess: false,
  });

  const getAdminFunctions = () => {
    const allFunctions = [
      {
        category: "School Management",
        items: [
          {
            name: "Create Schools",
            key: "createSchools",
            icon: FaSchool,
            description: "Create new schools in batch",
            color: "#6366f1",
            alpacaStaffOnly: true
          }
        ]
      },
      {
        category: "Teacher Management",
        items: [
          {
            name: "Add Teachers",
            key: "addingTeacher",
            icon: FaUserPlus,
            description: "Add new teachers to the school",
            color: "#10b981"
          },
          {
            name: "Edit/Update Teacher",
            key: "editingTeacher",
            icon: FaUserEdit,
            description: "Update teacher information and contact details",
            color: "#f59e0b"
          },
          {
            name: "Remove Teacher from School",
            key: "removingTeacher",
            icon: FaUserMinus,
            description: "Remove teachers from the school system",
            color: "#ef4444"
          }
        ]
      },
      {
        category: "Feature Access Control",
        items: [
          {
            name: "Feature & Tag Management",
            key: "schoolFeatureTags",
            icon: FaShieldAlt,
            description: "Manage feature access for this school",
            color: "#8b5cf6",
            alpacaStaffOnly: true,
          },
          {
            name: "Chatbot Access",
            key: "teacherChatbotAccess",
            icon: Bot,
            description: "Control chatbot access per teacher",
            color: "#0ea5e9",
            alpacaStaffOnly: true,
          }
        ]
      },
      {
        category: "Sales & Trials",
        alpacaStaffOnly: true,
        items: [
          {
            name: "Trial Management",
            key: "trialManagement",
            icon: Clock,
            description: "Manage school trials and trial periods",
            color: "#14b8a6",
            isNavigation: true,
            route: "/trialmanagement"
          },
          {
            name: "Purchase Management",
            key: "purchaseManagement",
            icon: CreditCard,
            description: "Manage school purchases and subscriptions",
            color: "#22c55e",
            isNavigation: true,
            route: "/purchasemanagement"
          },
          {
            name: "Bulk Grade Transition",
            key: "bulkGradeTransition",
            icon: FaGraduationCap,
            description: "Transition all classes of a grade level to the next grade",
            color: "#8b5cf6",
          }
        ]
      },
      {
        category: "Diagnostics & Support",
        alpacaStaffOnly: true,
        items: [
          {
            name: "Temporary School Access",
            key: "switchSchoolAccess",
            icon: LogIn,
            description: "Temporarily access a school environment for diagnosis (POC)",
            color: "#dc2626"
          }
        ]
      },
      {
        category: "Administration",
        items: [
          {
            name: "Change ALPACA Lead",
            key: "changingAlpacaLead",
            icon: FaUserCog,
            description: "Update the ALPACA lead for the school",
            color: "#3b82f6"
          }
        ]
      },
      {
        category: "Communication & Analytics",
        items: [
          {
            name: "Send Forgotten Password Email",
            key: "sendForgottenPassword",
            icon: FaEnvelope,
            description: "Send password reset emails to users",
            color: "#06b6d4"
          }
        ]
      }
    ];

    if (teacherMode) {
      return allFunctions
        .filter(category => !category.alpacaStaffOnly)
        .map(category => ({
          ...category,
          items: category.items.filter(item => !item.alpacaStaffOnly)
        }))
        .filter(category => category.items.length > 0);
    }

    return allFunctions;
  };

  const adminFunctions = getAdminFunctions();

  useEffect(() => {
    const fetchUserAndSchoolInfo = async () => {
      try {
        const response = await whoami();
        console.log("=== WHOAMI DEBUG ===");
        console.log("Full whoami response:", response);
        console.log("response.data:", response.data);
        console.log("response.data.stakeholder:", response.data.stakeholder);
        console.log("All stakeholder keys:", Object.keys(response.data.stakeholder));
        console.log("User roles:", response.data.roles);
        console.log("===================");

        const stakeholder = response.data.stakeholder;
        setTeacherid(stakeholder.id);
        setName(stakeholder.name);

        if (stakeholder.schoolid) {
          setStaffCurrentSchoolId(stakeholder.schoolid);
        }

        if (teacherMode) {
          const teacherSchoolId = stakeholder.schoolid || stakeholder.schoolId;

          if (!teacherSchoolId) {
            console.error("❌ Teacher has no school ID in their profile!");
            setSchoolName("Error: No school assigned to your account");
            setLoadError("No school assigned to your account");
            setIsLoadingSchool(false);
            return;
          }

          console.log("🏫 Teacher mode: Auto-loading school ID:", teacherSchoolId);
          setSchoolid(teacherSchoolId);

          try {
            const response = await getSchools();
            const schools = response.data || response;
            const teacherSchool = Array.isArray(schools)
              ? schools.find(s => s.id === teacherSchoolId)
              : null;

            if (teacherSchool) {
              setSchoolName(teacherSchool.name);
              setRollnumber(teacherSchool.rollnumber || "");

              const countryIdFromSchool = teacherSchool.countryid || teacherSchool.countryId || teacherSchool.country_id;
              if (countryIdFromSchool) {
                setCountryId(countryIdFromSchool);
                console.log("✅ CountryId from school:", countryIdFromSchool);
              }

              console.log("✅ Loaded teacher's school:", teacherSchool.name);
            } else {
              setSchoolName("School not found in database");
              setLoadError("School not found in database");
              console.error("❌ School not found for ID:", teacherSchoolId);
            }
          } catch (error) {
            console.error("❌ Error fetching school details:", error);
            setSchoolName("Error loading school details");
            setLoadError(`Error loading school: ${error.message}`);
          }

          setIsLoadingSchool(false);
        } else {
          const countryId = stakeholder.countryId ||
            stakeholder.country_id ||
            stakeholder.Country ||
            stakeholder.countryID ||
            stakeholder.countrycode ||
            stakeholder.countryCode;

          console.log("Final countryId value being set:", countryId);
          if (countryId) {
            setCountryId(countryId);
          }

          setSchoolName("Please enter school roll number and press 'Find School'");
          setIsLoadingSchool(false);
        }
      } catch (error) {
        console.error("❌ Error in whoami:", error);
        setSchoolName("Error loading user information");
        setLoadError(`Error loading user: ${error.message}`);
        setIsLoadingSchool(false);
      }
    };

    fetchUserAndSchoolInfo();
  }, [teacherMode]);

  useEffect(() => {
    console.log("SchoolAdmin countryId state changed to:", countryId);
  }, [countryId]);

  useEffect(() => {
    if (isSchoolFound) {
      fetchGradeLevel3Access();
    }
  }, [schoolid]);

  const fetchGradeLevel3Access = async () => {
    setGradeLevel3Loading(true);
    setGradeLevel3Error(null);
    try {
      const data = await getGradeLevel3FeatureAccessBySchoolId(schoolid);
      setGradeLevel3Access(Array.isArray(data) ? data : []);
      console.log("GradeLevel3 access data:", data);
    } catch (error) {
      console.error("Error fetching GradeLevel3 access:", error);
      setGradeLevel3Error(error.message);
      setGradeLevel3Access([]);
    } finally {
      setGradeLevel3Loading(false);
    }
  };

  const handleEnableGradeLevel3 = async () => {
    try {
      setGradeLevel3Loading(true);
      await createGradeLevel3FeatureAccess(schoolid);
      console.log("GradeLevel3 access enabled successfully");
      await fetchGradeLevel3Access();
    } catch (error) {
      console.error("Error enabling GradeLevel3 access:", error);
      setGradeLevel3Error(error.message);
    } finally {
      setGradeLevel3Loading(false);
    }
  };

  const handleDisableGradeLevel3 = async (accessId) => {
    try {
      setGradeLevel3Loading(true);
      await deactivateGradeLevel3FeatureAccess(accessId);
      console.log("GradeLevel3 access disabled successfully");
      await fetchGradeLevel3Access();
    } catch (error) {
      console.error("Error disabling GradeLevel3 access:", error);
      setGradeLevel3Error(error.message);
    } finally {
      setGradeLevel3Loading(false);
    }
  };

  const hasActiveGradeLevel3 = gradeLevel3Access.some(access => access.active);

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window.innerWidth < window.innerHeight);
    };

    window.addEventListener("resize", handleOrientationChange);
    return () => {
      window.removeEventListener("resize", handleOrientationChange);
    };
  }, []);

  const handleOpenModal = (modalName) => {
    setShowModal({ ...showModal, [modalName]: true });
  };

  const handleCloseModal = (modalName) => {
    setShowModal({ ...showModal, [modalName]: false });
  };

  const handleSchoolsCreated = (newSchools) => {
    console.log('Schools created successfully:', newSchools);
  };

  const AdminFunctionCard = ({ item }) => {
    const handleCardClick = () => {
      if (item.isNavigation) {
        if (schoolid) {
          navigate(`${item.route}?schoolId=${schoolid}&schoolName=${encodeURIComponent(schoolName)}`);
        } else {
          navigate(item.route);
        }
      } else {
        handleOpenModal(item.key);
      }
    };

    return (
      <Card
        className="class-admin-card"
        onClick={handleCardClick}
        style={{ cursor: 'pointer' }}
      >
        <Card.Body className="class-admin-card-body">
          <div className="class-admin-icon" style={{ backgroundColor: `${item.color}15`, color: item.color }}>
            <item.icon size={20} />
          </div>
          <div className="class-admin-content">
            <h6 className="class-admin-title">{item.name}</h6>
            <p className="class-admin-description">{item.description}</p>
          </div>
        </Card.Body>
      </Card>
    );
  };

  return (
    <div>
      <Container fluid className="class-admin-container">
        <HeadingBanner
          name={name}
          isLoggedIn={true}
          cloud={true}
          teacherid={teacherid}
        />

        <Row className="class-admin-row">
          <LeftPanel />
          <Col sm={{ span: 10, offset: 2 }} className="class-admin-content">

            <div className="class-admin-header">
              <h2 className="class-admin-page-title">
                {teacherMode ? "School Administration" : "School Administration"}
              </h2>
              <p className="class-admin-page-subtitle">
                {teacherMode
                  ? "Manage teachers, roles, and settings for your school"
                  : "Manage teachers, roles, and school settings"
                }
              </p>
            </div>

            {/* ✅ ROLE INDICATOR - Helpful for debugging */}
            {/* {teacherMode && (
              <Alert variant="info" className="mb-4">
                <strong>👨‍🏫 School Administrator Mode</strong>
                <p className="mb-0">You are managing your school. All actions are restricted to your school only.</p>
              </Alert>
            )} */}

            {/* ✅ ERROR DISPLAY */}
            {loadError && (
              <Alert variant="danger" className="mb-4">
                <strong>⚠️ Error Loading School</strong>
                <p className="mb-0">{loadError}</p>
              </Alert>
            )}

            {/* ✅ CREATE SCHOOLS - Only show for alpaca-staff */}
            {!teacherMode && (
              <div className="mb-4">
                <Card
                  className="class-admin-card"
                  onClick={() => handleOpenModal('createSchools')}
                  style={{ cursor: 'pointer', maxWidth: '400px' }}
                >
                  <Card.Body className="class-admin-card-body">
                    <div className="class-admin-icon" style={{ backgroundColor: '#6366f115', color: '#6366f1' }}>
                      <FaSchool size={20} />
                    </div>
                    <div className="class-admin-content">
                      <h6 className="class-admin-title">Create Schools (Batch)</h6>
                      <p className="class-admin-description">Add new schools to the database</p>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            )}

            {/* ✅ SCHOOL SELECTOR - Show search for alpaca-staff, show locked school for teachers */}
            <div className="school-selector-section">
              {teacherMode ? (
                // ✅ TEACHER VIEW: Show their school (locked, no search)
                <div className="selected-school locked-school">
                  <Alert variant="secondary" className="mb-3">
                    <FaSchool className="me-2" />
                    You are managing the following school:
                  </Alert>
                  {isLoadingSchool ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading your school information...</p>
                    </div>
                  ) : (
                    <>
                      <h3 className="school-name">{schoolName}</h3>
                      {rollnumber && (
                        <p className="text-muted">Roll Number: {rollnumber}</p>
                      )}
                      {/* ✅ Only show School ID for alpaca-staff */}
                      {!teacherMode && schoolid && (
                        <p className="text-muted">School ID: {schoolid}</p>
                      )}
                    </>
                  )}
                </div>
              ) : (
                // ✅ ALPACA-STAFF VIEW: Show search interface
                <>
                  <SchoolRollNumberInput
                    onSchoolIdRetrieved={(id) => {
                      console.log("School ID retrieved:", id);
                      setSchoolid(id);
                    }}
                    schoolNameRetrieved={(name, rollNum) => {
                      console.log("School name retrieved:", name, "Roll number:", rollNum);
                      setSchoolName(name);
                      if (rollNum) setRollnumber(rollNum);
                    }}
                  />
                  <div className="selected-school">
                    <h3 className="school-name">{schoolName}</h3>
                    {rollnumber && (
                      <p className="text-muted">Roll Number: {rollnumber}</p>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* GradeLevel3 Feature Access Card - Only show for alpaca-staff */}
            {isSchoolFound && !teacherMode && (
              <div className="mb-4">
                <Card style={{ borderTop: '4px solid #8b5cf6' }}>
                  <Card.Body>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h5 style={{ marginBottom: '0.5rem' }}>GradeLevel3 Feature Access</h5>
                        <p className="text-muted" style={{ marginBottom: 0 }}>
                          {gradeLevel3Loading
                            ? 'Loading...'
                            : hasActiveGradeLevel3
                              ? '✓ Active'
                              : 'Not enabled'}
                        </p>
                      </div>
                      <div>
                        {!gradeLevel3Loading && !hasActiveGradeLevel3 && (
                          <Button
                            variant="success"
                            size="sm"
                            onClick={handleEnableGradeLevel3}
                          >
                            Enable Access
                          </Button>
                        )}
                        {!gradeLevel3Loading && hasActiveGradeLevel3 && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                              const activeAccess = gradeLevel3Access.find(a => a.active);
                              if (activeAccess) handleDisableGradeLevel3(activeAccess.id);
                            }}
                          >
                            Disable Access
                          </Button>
                        )}
                      </div>
                    </div>
                    {gradeLevel3Error && (
                      <p style={{ color: '#ef4444', marginTop: '0.5rem', marginBottom: 0 }}>
                        Error: {gradeLevel3Error}
                      </p>
                    )}
                  </Card.Body>
                </Card>
              </div>
            )}

            {/* Only show admin functions if school has been found */}
            {isSchoolFound && (
              <div className="admin-functions-container">
                {adminFunctions.map((category, categoryIndex) => (
                  <div key={categoryIndex} className="function-category">
                    <h4 className="category-title">{category.category}</h4>
                    <div className="function-cards-grid">
                      {category.items.map((item, itemIndex) => (
                        <AdminFunctionCard key={itemIndex} item={item} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Optional: Show a message when no school is selected */}
            {!isSchoolFound && !isLoadingSchool && (
              <div className="no-school-message" style={{
                textAlign: 'center',
                padding: '2rem',
                color: '#6b7280',
                fontSize: '1.1rem'
              }}>
                {teacherMode
                  ? loadError || "Unable to load school information. Please contact support."
                  : "Please select a school to access administration functions."
                }
              </div>
            )}

          </Col>
        </Row>
      </Container>

      {isPortrait && <OrientationPrompt />}

      {/* ✅ CreateSchools Modal - Only render for alpaca-staff */}
      {!teacherMode && (
        <CreateSchoolsModal
          show={showModal.createSchools}
          onHide={() => handleCloseModal('createSchools')}
          getCountries={getCountries}
          createSchoolsBatch={createSchoolsBatch}
          onSchoolsCreated={handleSchoolsCreated}
        />
      )}

      {/* ✅ SwitchSchoolAccess Modal - Only render for alpaca-staff */}
      <SwitchSchoolAccessModal
        show={showModal.switchSchoolAccess}
        onHide={() => handleCloseModal('switchSchoolAccess')}
        teacherId={teacherid}
        currentSchoolId={staffCurrentSchoolId}
        schoolId={schoolid}
        schoolName={schoolName}
      />

      {/* All the existing modals - only render if school is found */}
      {isSchoolFound && (
        <>
          {showModal.addingTeacher && (
            <AddTeachersModal
              show={showModal.addingTeacher}
              onHide={() => handleCloseModal('addingTeacher')}
              schoolid={schoolid}
              rollnumber={rollnumber}
              teacherName={name}
              addAdditionalColleagues={addAdditionalColleagues}
              getTeachers={getTeachersBySchoolId}
              getClasses={getClassesBySchoolId}
              getRoles={getRoles}
              getSchools={getSchools}
              currentTeacherId={teacherid}
            />
          )}

          {showModal.editingTeacher && (
            <EditTeacherModal
              show={showModal.editingTeacher}
              onHide={() => handleCloseModal('editingTeacher')}
              schoolid={schoolid}
            />
          )}

          {showModal.removingTeacher && (
            <RemovingTeacherModal
              show={showModal.removingTeacher}
              onHide={() => handleCloseModal('removingTeacher')}
              schoolid={schoolid}
              currentTeacherId={teacherid.toString()}
              currentTeacherName={name}
            />
          )}

          {showModal.changingAlpacaLead && (
            <ChangeAlpacaLeadModal
              show={showModal.changingAlpacaLead}
              onHide={() => handleCloseModal('changingAlpacaLead')}
              schoolid={schoolid}
            />
          )}

          {showModal.bulkGradeTransition && (
            <BulkGradeTransitionModal
              show={showModal.bulkGradeTransition}
              onHide={() => handleCloseModal('bulkGradeTransition')}
              schoolId={schoolid}
            />
          )}

          <SchoolFeatureTagsModal
            show={showModal.schoolFeatureTags}
            onHide={() => handleCloseModal('schoolFeatureTags')}
            schoolId={schoolid}
            schoolName={schoolName}
            userName={name}
          />

          <TeacherChatbotAccessModal
            show={showModal.teacherChatbotAccess}
            onHide={() => handleCloseModal('teacherChatbotAccess')}
            schoolId={schoolid}
            schoolName={schoolName}
            userName={name}
          />

          <Modal show={showModal.rolesPermissions} onHide={() => handleCloseModal('rolesPermissions')}>
            <Modal.Header closeButton>
              <Modal.Title>Roles and Permissions Management</Modal.Title>
            </Modal.Header>
            <Modal.Body>Content for roles and permissions management.</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => handleCloseModal('rolesPermissions')}>Close</Button>
            </Modal.Footer>
          </Modal>

          <Modal show={showModal.sendForgottenPassword} onHide={() => handleCloseModal('sendForgottenPassword')}>
            <Modal.Header closeButton>
              <Modal.Title>Send Forgotten Password Email</Modal.Title>
            </Modal.Header>
            <Modal.Body>Content for sending forgotten password emails.</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => handleCloseModal('sendForgottenPassword')}>Close</Button>
            </Modal.Footer>
          </Modal>

          <Modal show={showModal.viewMetrics} onHide={() => handleCloseModal('viewMetrics')}>
            <Modal.Header closeButton>
              <Modal.Title>View School Metrics</Modal.Title>
            </Modal.Header>
            <Modal.Body>Content for viewing school metrics.</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => handleCloseModal('viewMetrics')}>Close</Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </div>
  );
}

export default SchoolAdmin;